import { useState, useEffect, useMemo } from "react"
import { Box, Card, Grid, TextField, Typography, IconButton, MenuItem, Select, FormControl, InputLabel, useTheme } from "@mui/material"
import { MapBase, MarkerFibraNaps, MarkerFibraCliente } from "../../components"
import KmzService from "../../services/kmz.service"
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import AuxiliarFunction from "../../function/AuxiliarFunction"

const MapaFibraOpticaAllScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [items, setItems] = useState([]);
    const [localidadFiltro, setLocalidadFiltro] = useState('');
    const [coordenadas, setCoordenadas] = useState({texto:'',lat: null,lng: false})
    const [isLoadingPrefa, setIsLoadingPrefa] = useState(false);
    const [rangoRadio, setRangoRadio] = useState(80);
    const [prefactibilidadLocation, setPrefactibilidadLocation] = useState({
        lat: -24.789251,
        lng: -65.410393
    });
    const [mapLocation, setMapLocation] = useState({
        lat: -24.789251,
        lng: -65.410393
    });
    const [prefactibilidad, setPrefactibilidad] = useState(null);

    const actualizar = async() =>{
        await KmzService.obtenerNapsFO().then((resp)=>{
            setItems(resp);
        })
    }

    // Obtener localidades únicas del array de items
    const localidadesUnicas = useMemo(() => {
        if (!Array.isArray(items) || items.length === 0) return [];
        
        const localidades = items
            .map(item => item.localidad)
            .filter(localidad => localidad) // Filtrar valores nulos o undefined
            .sort(); // Ordenar alfabéticamente
        
        return [...new Set(localidades)]; // Eliminar duplicados
    }, [items]);

    // Filtrar items por localidad seleccionada
    const itemsFiltrados = useMemo(() => {
        if (!localidadFiltro) return items;
        return items.filter(item => item.localidad === localidadFiltro);
    }, [items, localidadFiltro]);

    useEffect(() => {
        actualizar()
    }, [])

    const onPrefa = async(newCoord) =>{
        setCoordenadas({texto:`${newCoord.lat}, ${newCoord.lng}`,lat: newCoord.lat, lng:newCoord.lng})
        setIsLoadingPrefa(true)
        await KmzService.coberturaFO({lat: newCoord.lat, lng:newCoord.lng}).then((r)=>{
            setPrefactibilidad(r);
            if(r?.rango){
                setRangoRadio(r?.rango)
            }
            setIsLoadingPrefa(false)
        }).catch((err)=>{
            setIsLoadingPrefa(false)
        })
    }

    const onClickMap = async(newCoord,moverCamara) => {
        if(moverCamara){
            setMapLocation(newCoord)
        }
        await onPrefa(newCoord)
        setPrefactibilidadLocation(newCoord);
    }

    const styleInputHeight = useMemo(() => ({
        '& .MuiInputLabel-root':{
            color: isDarkMode ? '#b0bec5' : 'gray',
            fontSize: 15
        },
        '& fieldset': {
            borderRadius: 2,
            border: 1,
            borderColor: isDarkMode ? '#3d4a62' : 'gray',
            height: 45
        },
        '& label':{
            marginTop: -0.5,
        },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                border:1,
                height: 45,
                borderColor: isDarkMode ? '#90caf9' : '#0364ff'
            },
            '&:hover':{
                border: 1,
                borderRadius: 2,
                borderColor: isDarkMode ? '#3d4a62' : 'gray',
                height: 40
            },
            '&.Mui-focused': {
                '&:hover':{
                border:0,
                height: 40
                }
            },
            color: isDarkMode ? '#e0e0e0' : 'gray',
            height: 40
        }
    }), [isDarkMode]);

    const onSubmitCoordenadas = () =>{
        setIsLoadingPrefa(true)
        const quitarEspacios = coordenadas.texto.replace(/ /g, "")
        const separador = quitarEspacios.split(',')
        if(separador.length === 2){
            const validCoord = `${separador[0]}, ${separador[1]}`
            const valid = AuxiliarFunction.isValidCoordinates(validCoord)
            if(valid){
                setIsLoadingPrefa(false)
                onClickMap({lat:Number(separador[0]), lng:Number(separador[1])},true)
                setCoordenadas({texto: coordenadas.texto, estadoErr: false,lat: separador[0], lng:separador[1]})
            }else{
                setIsLoadingPrefa(false)
                setCoordenadas({texto: coordenadas.texto, estadoErr: true,lat: coordenadas.lat, lng:coordenadas.lng})
            }
        }else{
            setIsLoadingPrefa(false)
            setCoordenadas({texto: coordenadas.texto, estadoErr: true,lat: coordenadas.lat, lng:coordenadas.lng})
        }
    }
    useEffect(() => {
        document.title = `Cobertura FO`;
    }, [])

    return (
        <Box sx={{width:'100%',height: '100%',overflow:'auto',backgroundColor: isDarkMode ? '#1a2332' : '#f5f5f5'}} className='side'>
            <Grid container p={3} display='flex' justifyContent='center' spacing={1}>
                <Grid item xs={12} md={4}>
                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000', fontWeight: 'bold'}} variant="h2">Mapa fibra optica</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{background: isDarkMode ? '#2d3a52 !important' : '#fff !important',p:2,borderRadius: 5}}>
                        <FormControl fullWidth sx={styleInputHeight}>
                            <InputLabel 
                                id="localidad-select-label"
                                sx={{
                                    color: isDarkMode ? '#b0bec5' : 'gray',
                                    fontSize: 15,
                                    marginTop: -0.5,
                                    '&.Mui-focused': {
                                        color: isDarkMode ? '#90caf9' : '#0364ff'
                                    }
                                }}
                            >
                                Filtrar por Localidad
                            </InputLabel>
                            <Select
                                labelId="localidad-select-label"
                                id="localidad-select"
                                value={localidadFiltro}
                                label="Filtrar por Localidad"
                                onChange={(e) => setLocalidadFiltro(e.target.value)}
                                sx={{
                                    height: 40,
                                    color: isDarkMode ? '#e0e0e0' : 'inherit',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? '#3d4a62' : 'gray',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? '#3d4a62' : 'gray',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: isDarkMode ? '#90caf9' : '#0364ff',
                                    }
                                }}
                            >
                                <MenuItem value="">
                                    <em>Todas las localidades</em>
                                </MenuItem>
                                {localidadesUnicas.map((localidad) => (
                                    <MenuItem key={localidad} value={localidad}>
                                        {localidad}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{background: isDarkMode ? '#2d3a52 !important' : '#fff !important',p:2,borderRadius: 5}}>
                        <TextField
                            fullWidth
                            autoComplete='off'
                            sx={styleInputHeight}
                            InputLabelProps={{shrink: true}}
                            value={coordenadas?.texto}
                            onChange={(e)=>setCoordenadas({texto: e.target.value, estadoErr: false,lat: coordenadas.lat, lng:coordenadas.lng})}
                            InputProps={{
                                endAdornment: (
                                <IconButton onClick={()=>onSubmitCoordenadas()}>
                                    <GpsFixedIcon sx={{color: isDarkMode ? '#90caf9' : '#000'}} size={35} />
                                </IconButton>
                                ),
                            }}
                        />
                        {coordenadas?.estadoErr && (
                            <Typography sx={{color: isDarkMode ? '#f87171' : 'red',fontSize:10,marginLeft:2}}>No son unas coordenadas validas</Typography>
                        )}
                    </Card>
                </Grid>
                {/* <Grid item xs={6} sx={{display: 'flex', justifyContent: 'end'}}>
                    <ButtonBasic
                        textButton="NUEVO"
                        sx={{width: 200, height: 30}}
                        onClick={()=>onOpenModal()}
                        variant='wk'
                        color="nextStep"
                    />
                </Grid> */}
                <Grid item xs={12} md={3} mt={2}>
                    <Card sx={{background: isDarkMode ? '#2d3a52 !important' : '#fff !important',minHeight: 250,borderRadius:2,boxShadow: 3,p:1}}>
                        {prefactibilidad?.cobertura && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000',textAlign: 'center',fontWeight: 'bold'}} variant="h4">{`${prefactibilidad?.cobertura}`}</Typography>
                                </Grid>
                                <Grid item xs={12} md={12} mb={-1}>
                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000',textAlign: 'center',fontWeight: 'bold'}} variant="h6">{`Radio: ${prefactibilidad?.rango}`}</Typography>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000',textAlign: 'center'}} variant="h6">{`Coordenadas: ${coordenadas.lat},${coordenadas.lng}`}</Typography>
                                </Grid>
                                {Array.isArray(prefactibilidad?.naps) && prefactibilidad?.naps?.length > 0 && (
                                    prefactibilidad?.naps.map((it)=>(
                                        <Grid item xs={4} md={6} key={it?.nombre_fnap}>
                                            <Card 
                                                sx={{
                                                    background: isDarkMode ? '#1F2A40 !important' : '#fff !important', 
                                                    boxShadow: 3, 
                                                    borderRadius: 2,
                                                    p:1,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: 5,
                                                        transform: 'scale(1.02)',
                                                        borderColor: isDarkMode ? '#90caf9' : '#0364ff',
                                                        border: `2px solid ${isDarkMode ? '#90caf9' : '#0364ff'}`
                                                    }
                                                }}
                                                onClick={() => {
                                                    const napCoords = {
                                                        lat: Number(it?.latitud_fnap),
                                                        lng: Number(it?.longitud_fnap)
                                                    };
                                                    setMapLocation(napCoords);
                                                }}
                                            >
                                                <Box sx={{display: 'flex', flexDirection: 'row', marginTop:2}}>
                                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Nombre: <Box fontWeight='bold' display='inline'>{it?.nombre_fnap}</Box></Typography>
                                                </Box>
                                                <Box sx={{display: 'flex', flexDirection: 'row', marginTop:2}}>
                                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Distancia: <Box fontWeight='bold' display='inline'>{it?.distanciaReal|| it?.distanciaMts}</Box> Mts</Typography>
                                                </Box>
                                                <Box sx={{display: 'flex', flexDirection: 'row', marginTop:1}}>
                                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Zona: <Box fontWeight='bold' display='inline'>{it?.sector|| '-'}</Box></Typography>
                                                </Box>
                                                <Box sx={{display: 'flex', flexDirection: 'row', marginTop:1}}>
                                                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000'}} component='div'>Puertos Usados: <Box fontWeight='bold' display='inline'>{`${it?.usados}/${it?.puertos}`}</Box></Typography>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                        )}
                    </Card>
                </Grid>
                <Grid item xs={12} md={9} mt={2}>
                    <Card sx={{background: isDarkMode ? '#2d3a52 !important' : '#fff !important', boxShadow: 3, borderRadius:2,p:1}}>
                        <MapBase
                            mapContainerStyle={{
                                width: '100%',
                                height: 510
                            }}
                            onClick={(e)=>onClickMap(e,true)}
                            isSearchable={false}
                            options={{
                                maxZoom: 23,
                                minZoom: 8,
                            }}
                            zoom={19}
                            coordinates={mapLocation}
                        >
                            <MarkerFibraCliente
                                item={prefactibilidadLocation}
                                key='onCLickMapFO'
                                rangoBd={rangoRadio}
                            />
                            {(Array.isArray(itemsFiltrados) && itemsFiltrados?.length > 0) && (
                                itemsFiltrados.map((it)=>{
                                    // Verificar si esta NAP está en la lista de cobertura
                                    const isInCoverage = prefactibilidad?.naps?.find((nap) => nap?.nombre === it?.nombre);
                                    return (
                                        <MarkerFibraNaps
                                            coordenadas={{lat: Number(it?.latitud),lng: Number(it?.longitud)}}
                                            key={it?.nombre}
                                            item={it}
                                            isInCoverage={isInCoverage}
                                        />
                                    )
                                })
                            )}
                        </MapBase>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

export default MapaFibraOpticaAllScreen